import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable } from 'jotai/utils';
import { INoteDataModel } from '../../data-models/note.data-model';
import {
  AuditorDateRangesPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../data-models/preferences-v3.data-model';
import {
  ActivityTabsType,
  selectedActivityNotesJ,
  selectedCompanyIdProfileAtom,
} from '../../pages/CompanyProfiles/state/UIStateJ';
import { preferenceAtom } from '../../util/preferences-service-v3';
import { getAllNotesForCompany } from '../queries/MaggieNoteQueries';
import { PermissionService } from '../PermissionService';
import { authenticatedUserState } from './AuthState';

const auditorDateRangesPreferenceAtom = preferenceAtom<AuditorDateRangesPreferences>({
  preferenceKey: PreferenceKey.auditorDateRanges,
  scope: PreferenceScope.ORGANIZATION,
});

type NotesValue = INoteDataModel<unknown>[] | Promise<INoteDataModel<unknown>[]>;

/**
 * The default fetches all notes for the company. Consumers using
 * `useAtomValue` will suspend; callers needing a sync read should wrap with
 * `unwrap(notesStateJ(companyId))`.
 */
export const notesStateJ = atomFamily((companyId: number) =>
  atomWithDefault<NotesValue>(async () => {
    const { notes } = await getAllNotesForCompany(companyId);
    return notes;
  })
);

export const notesStateLoadableJ = atomFamily((companyId: number) => loadable(notesStateJ(companyId)));

/**
 * Notes for a given company with auditor date-range filtering applied. For
 * auditors with date restrictions enabled, only returns notes within the
 * configured date range; for non-auditors or auditors without restrictions,
 * returns all notes.
 */
export const filteredNotesByCompanyStateJ = atomFamily((companyId: number) =>
  atom(async (get) => {
    const notes = await get(notesStateJ(companyId));

    const isAuditor = PermissionService.get().isAuditor();
    if (!isAuditor) {
      return notes;
    }

    const preferences = get(auditorDateRangesPreferenceAtom);
    if (!preferences?.restrictToRange) {
      return notes;
    }

    const startDate = new Date(preferences.startDate);
    const endDate = new Date(preferences.endDate);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return notes;
    }

    return notes.filter((note) => {
      if (!note.createdAt) {
        return false;
      }
      const noteDate = new Date(note.createdAt);
      return noteDate >= startDate && noteDate <= endDate;
    });
  })
);

/**
 * Notes for the currently-selected company in the activity drawer, with
 * **both** auditor date-range filtering and the active activity tab
 * (`ALL` vs `MINE`) applied, sorted newest-first.
 *
 * Layers on top of {@link filteredNotesByCompanyStateJ} so auditor restrictions
 * apply uniformly in the activity drawer — the constraint is security /
 * compliance, not a UI concern.
 */
export const activityFilteredNotesAtomJ = atom(async (get) => {
  const companyId = get(selectedCompanyIdProfileAtom);
  if (companyId < 0) {
    return [];
  }

  const notes = await get(filteredNotesByCompanyStateJ(companyId));
  const selectedActivityTab = get(selectedActivityNotesJ);
  const currentUser = get(authenticatedUserState);

  const visibleNotes =
    selectedActivityTab === ActivityTabsType.ALL
      ? notes.slice()
      : notes.filter((note) => note.createdBy === currentUser?.email);

  return visibleNotes.sort(sortNotesByDate);
});

function sortNotesByDate(note1: INoteDataModel<unknown>, note2: INoteDataModel<unknown>) {
  const date1 = new Date(note1.updatedAt);
  const date2 = new Date(note2.updatedAt);
  return date2.getTime() - date1.getTime();
}
